// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { clampPointToRect, distanceSq, pointInsideRect, simplifyPolyline } from '../../utils/geometry'
import Konva from 'konva'

interface ImageSize {
    w: number
    h: number
}

interface ContainerSize {
    w: number
    h: number
}

interface Rect {
    x: number
    y: number
    w: number
    h: number
}

export interface UseMaskDrawingResult {
    stageRef: React.RefObject<Konva.Stage | null>
    maskLayerRef: React.RefObject<Konva.Layer | null>
    uiLayerRef: React.RefObject<Konva.Layer | null>
    maskContentGroupRef: React.RefObject<Konva.Group | null>
    uiContentGroupRef: React.RefObject<Konva.Group | null>
    containerSize: ContainerSize
    imageRectInContainer: Rect | null
    brushEnabled: boolean
    setBrushEnabled: (v: boolean | ((prev: boolean) => boolean)) => void
    brushDiameterCssPx: number
    brushDiameterWorldPx: number
    setBrushRadiusImagePx: (v: number | ((prev: number) => number)) => void
    showBrush: boolean
    brushCenter: { x: number; y: number } | null
    canUndo: boolean
    canRedo: boolean
    hasStrokes: boolean
    maskHandlers: {
        onPointerDown: (e: Konva.KonvaEventObject<PointerEvent>) => void
        onPointerMove: (e: Konva.KonvaEventObject<PointerEvent>) => void
        onPointerUp: () => void
        onPointerLeave: () => void
    }
    maskActions: {
        undo: () => void
        redo: () => void
        reset: () => void
        exportMask: () => Promise<Blob | null>
    }
}

export function useMaskDrawing(
    imageNaturalSize: ImageSize | null,
    containerRef: React.RefObject<HTMLDivElement | null>,
    contentScale: number,
    contentOffset: { x: number; y: number }
): UseMaskDrawingResult {
    const [brushRadiusImagePx, setBrushRadiusImagePx] = useState<number>(128)
    const [brushEnabled, setBrushEnabled] = useState(false)
    const [containerSize, setContainerSize] = useState<ContainerSize>({ w: 0, h: 0 })
    const [imageRectInContainer, setImageRectInContainer] = useState<Rect | null>(null)

    const stageRef = useRef<Konva.Stage | null>(null)
    const maskLayerRef = useRef<Konva.Layer | null>(null)
    const uiLayerRef = useRef<Konva.Layer | null>(null)
    const maskContentGroupRef = useRef<Konva.Group | null>(null)
    const uiContentGroupRef = useRef<Konva.Group | null>(null)

    const isDrawingRef = useRef(false)
    const currentLineRef = useRef<Konva.Line | null>(null)
    const undoStackRef = useRef<Konva.Line[]>([])
    const redoStackRef = useRef<Konva.Line[]>([])
    const [, setHistoryVersion] = useState(0)
    const notifyHistoryChange = useCallback(() => setHistoryVersion((v) => v + 1), [])

    const scheduleRef = useRef<number | null>(null)
    const brushPosRef = useRef<{ x: number; y: number } | null>(null)
    const [brushCenter, setBrushCenter] = useState<{ x: number; y: number } | null>(null)
    const brushCenterRafRef = useRef<number | null>(null)
    const lastBrushCenterRef = useRef<{ x: number; y: number } | null>(null)
    const lastAppendRef = useRef<{ x: number; y: number } | null>(null)

    const UNDO_STACK_LIMIT = 100
    const MIN_APPEND_DISTANCE_SQ = 0.25
    const SIMPLIFY_TOLERANCE = 0.75

    useLayoutEffect(() => {
        const updateSizes = () => {
            const el = containerRef.current
            if (!el) return
            const w = el.clientWidth
            const h = el.clientHeight
            setContainerSize({ w, h })
        }
        updateSizes()
        const onResize = () => updateSizes()
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [containerRef])

    useEffect(() => {
        if (!imageNaturalSize) {
            setImageRectInContainer(null)
            return
        }
        const { w: cw, h: ch } = containerSize
        if (cw === 0 || ch === 0) return
        const imgAspect = imageNaturalSize.w / imageNaturalSize.h
        const containerAspect = cw / ch
        let w = cw
        let h = cw / imgAspect
        if (containerAspect > imgAspect) {
            h = ch
            w = ch * imgAspect
        }
        const x = (cw - w) / 2
        const y = (ch - h) / 2
        setImageRectInContainer({ x, y, w, h })
    }, [containerSize, imageNaturalSize])

    useEffect(() => {
        isDrawingRef.current = false
        currentLineRef.current = null
        for (const l of undoStackRef.current) l.destroy()
        for (const l of redoStackRef.current) l.destroy()
        undoStackRef.current = []
        redoStackRef.current = []
        maskLayerRef.current?.batchDraw()
        notifyHistoryChange()
    }, [imageNaturalSize, notifyHistoryChange])

    const batchDraw = useCallback(() => {
        if (scheduleRef.current) return
        scheduleRef.current = window.requestAnimationFrame(() => {
            scheduleRef.current = null
            maskLayerRef.current?.batchDraw()
        })
    }, [])

    const pointInsideImageRect = useCallback((x: number, y: number): boolean => {
        return pointInsideRect(imageRectInContainer, x, y)
    }, [imageRectInContainer])

    const clampToImageRect = useCallback((x: number, y: number): { x: number; y: number } | null => {
        return clampPointToRect(imageRectInContainer, x, y)
    }, [imageRectInContainer])

    const getPointerWorld = useCallback(() => {
        const stage = stageRef.current
        if (!stage) return null
        const pos = stage.getPointerPosition()
        if (!pos) return null
        const x = (pos.x - contentOffset.x) / (contentScale || 1)
        const y = (pos.y - contentOffset.y) / (contentScale || 1)
        return { x, y }
    }, [contentOffset, contentScale])

    const brushDiameterCssPx = useMemo(() => {
        if (!imageNaturalSize || !imageRectInContainer) return 0
        const scaleX = imageRectInContainer.w / imageNaturalSize.w
        return scaleX * brushRadiusImagePx * 2
    }, [imageNaturalSize, imageRectInContainer, brushRadiusImagePx])

    const brushDiameterWorldPx = useMemo(() => {
        if (!imageNaturalSize || !imageRectInContainer) return 0
        const worldPerImage = imageRectInContainer.w / imageNaturalSize.w
        return worldPerImage * brushRadiusImagePx * 2
    }, [imageNaturalSize, imageRectInContainer, brushRadiusImagePx])

    const onPointerDown = useCallback(() => {
        if (!brushEnabled || !imageRectInContainer) return
        const maskGroup = maskContentGroupRef.current
        if (!maskGroup) return
        const p = getPointerWorld()
        if (!p) return
        if (!pointInsideImageRect(p.x, p.y)) return
        brushPosRef.current = p
        setBrushCenter(p)
        isDrawingRef.current = true
        const worldPerImage = imageNaturalSize ? (imageRectInContainer.w / imageNaturalSize.w) : 1
        const strokeWidthWorld = Math.max(1, brushRadiusImagePx * 2 * worldPerImage)
        const line = new Konva.Line({
            points: [p.x, p.y],
            stroke: 'rgba(255,10,92,0.5)',
            strokeWidth: strokeWidthWorld,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
        })
        maskGroup.add(line)
        currentLineRef.current = line
        lastAppendRef.current = p
        for (const l of redoStackRef.current) l.destroy()
        redoStackRef.current = []
        if (undoStackRef.current.length >= UNDO_STACK_LIMIT) {
            const removed = undoStackRef.current.shift()
            removed?.destroy()
        }
        undoStackRef.current.push(line)
        batchDraw()
        notifyHistoryChange()
    }, [batchDraw, brushEnabled, getPointerWorld, imageRectInContainer, pointInsideImageRect, brushRadiusImagePx, imageNaturalSize, notifyHistoryChange])

    const onPointerMove = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
        const native = e?.evt as PointerEvent | undefined
        const p = getPointerWorld()
        if (p) {
            brushPosRef.current = p
            if (!brushCenterRafRef.current) {
                brushCenterRafRef.current = window.requestAnimationFrame(() => {
                    brushCenterRafRef.current = null
                    const pos = brushPosRef.current
                    if (!pos) return
                    const prev = lastBrushCenterRef.current
                    if (!prev || prev.x !== pos.x || prev.y !== pos.y) {
                        setBrushCenter(pos)
                        lastBrushCenterRef.current = pos
                    }
                })
            }
        }
        if (!isDrawingRef.current || !brushEnabled) return
        if (!p) return
        const clamped = clampToImageRect(p.x, p.y)
        if (!clamped) return
        const line = currentLineRef.current
        if (!line) return
        const pts = line.points()

        const coalesced = typeof (native as any)?.getCoalescedEvents === 'function'
            ? ((native as any).getCoalescedEvents() as PointerEvent[] | undefined)
            : undefined
        if (coalesced && coalesced.length > 0) {
            for (const ce of coalesced) {
                const cworld = (() => {
                    const el = containerRef.current
                    if (!el) return null
                    const r = el.getBoundingClientRect()
                    const x = (ce.clientX - r.left - contentOffset.x) / (contentScale || 1)
                    const y = (ce.clientY - r.top - contentOffset.y) / (contentScale || 1)
                    return clampToImageRect(x, y)
                })()
                if (!cworld) continue
                const last = lastAppendRef.current
                if (!last || distanceSq(last.x, last.y, cworld.x, cworld.y) >= MIN_APPEND_DISTANCE_SQ) {
                    pts.push(cworld.x, cworld.y)
                    lastAppendRef.current = cworld
                }
            }
        } else {
            const last = lastAppendRef.current
            if (!last || distanceSq(last.x, last.y, clamped.x, clamped.y) >= MIN_APPEND_DISTANCE_SQ) {
                pts.push(clamped.x, clamped.y)
                lastAppendRef.current = clamped
            }
        }
        line.points(pts)
        batchDraw()
    }, [batchDraw, brushEnabled, clampToImageRect, getPointerWorld, containerRef, contentOffset.x, contentOffset.y, contentScale])

    const onPointerUp = useCallback(() => {
        isDrawingRef.current = false
        const line = currentLineRef.current
        if (line) {
            const simplified = simplifyPolyline(line.points(), SIMPLIFY_TOLERANCE)
            line.points(simplified)
            batchDraw()
        }
        currentLineRef.current = null
        lastAppendRef.current = null
    }, [batchDraw, SIMPLIFY_TOLERANCE])

    const onPointerLeave = useCallback(() => {
        if (isDrawingRef.current) {
            const line = currentLineRef.current
            if (line) {
                const simplified = simplifyPolyline(line.points(), SIMPLIFY_TOLERANCE)
                line.points(simplified)
                batchDraw()
            }
            isDrawingRef.current = false
            currentLineRef.current = null
            lastAppendRef.current = null
        }
    }, [batchDraw, SIMPLIFY_TOLERANCE])

    const undo = useCallback(() => {
        const last = undoStackRef.current.pop()
        if (!last) return
        redoStackRef.current.unshift(last)
        last.remove()
        if (redoStackRef.current.length > UNDO_STACK_LIMIT) {
            const removed = redoStackRef.current.pop()
            removed?.destroy()
        }
        batchDraw()
        notifyHistoryChange()
    }, [batchDraw, notifyHistoryChange])

    const redo = useCallback(() => {
        const item = redoStackRef.current.shift()
        if (!item) return
        maskLayerRef.current?.add(item)
        undoStackRef.current.push(item)
        batchDraw()
        notifyHistoryChange()
    }, [batchDraw, notifyHistoryChange])

    const reset = useCallback(() => {
        for (const l of undoStackRef.current) l.destroy()
        for (const l of redoStackRef.current) l.destroy()
        undoStackRef.current = []
        redoStackRef.current = []
        batchDraw()
        notifyHistoryChange()
    }, [batchDraw, notifyHistoryChange])

    const exportMask = useCallback(async (): Promise<Blob | null> => {
        const layer = maskLayerRef.current
        const group = maskContentGroupRef.current
        if (!layer || !group || !imageNaturalSize || !imageRectInContainer) return null
        const prevPos = { x: group.x(), y: group.y() }
        const prevScale = { x: group.scaleX(), y: group.scaleY() }
        group.position({ x: 0, y: 0 })
        group.scale({ x: 1, y: 1 })
        layer.batchDraw()
        const pixelRatio = imageNaturalSize.w / imageRectInContainer.w
        const strokeCanvas = layer.toCanvas({
            x: imageRectInContainer.x,
            y: imageRectInContainer.y,
            width: imageRectInContainer.w,
            height: imageRectInContainer.h,
            pixelRatio,
        })
        group.position(prevPos)
        group.scale(prevScale)
        layer.batchDraw()
        const out = document.createElement('canvas')
        out.width = imageNaturalSize.w
        out.height = imageNaturalSize.h
        const octx = out.getContext('2d')
        if (!octx) return null
        octx.clearRect(0, 0, out.width, out.height)
        octx.imageSmoothingEnabled = false
        octx.drawImage(strokeCanvas, 0, 0, out.width, out.height)
        const img = octx.getImageData(0, 0, out.width, out.height)
        const data = img.data
        for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3]
            const v = a >= 128 ? 255 : 0
            data[i] = v
            data[i + 1] = v
            data[i + 2] = v
            data[i + 3] = 255
        }
        octx.putImageData(img, 0, 0)
        return await new Promise<Blob | null>((resolve) => {
            out.toBlob((b) => resolve(b), 'image/png')
        })
    }, [imageNaturalSize, imageRectInContainer])

    const canUndo = undoStackRef.current.length > 0
    const canRedo = redoStackRef.current.length > 0
    const hasStrokes = undoStackRef.current.length > 0
    const showBrush = !!(brushEnabled && brushCenter && imageRectInContainer)

    return {
        stageRef,
        maskLayerRef,
        uiLayerRef,
        maskContentGroupRef,
        uiContentGroupRef,
        containerSize,
        imageRectInContainer,
        brushEnabled,
        setBrushEnabled,
        brushDiameterCssPx,
        brushDiameterWorldPx,
        setBrushRadiusImagePx,
        showBrush,
        brushCenter,
        canUndo,
        canRedo,
        hasStrokes,
        maskHandlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerLeave,
        },
        maskActions: {
            undo,
            redo,
            reset,
            exportMask,
        },
    }
}

